import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  IconButton,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import {
  Search,
  FilterList,
  CloudDownload,
  Visibility,
  Folder,
  InsertDriveFile,
  PictureAsPdf,
  Image,
  Description,
  Code,
  Archive,
  Sort,
  CalendarToday,
  Person,
  Refresh
} from '@mui/icons-material';
import { getNodes, downloadNode, searchNodes } from '../api/alfresco';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';

const DocumentSorting = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('modifiedAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [filterType, setFilterType] = useState('all');
  const [selectedNode, setSelectedNode] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [currentPath, setCurrentPath] = useState('-root-');
  const [breadcrumbs, setBreadcrumbs] = useState([{ id: '-root-', name: 'Racine' }]);

  // Charger les documents au montage
  useEffect(() => {
    loadDocuments();
  }, [currentPath]);

  const loadDocuments = async () => {
    console.log('🔍 Chargement des documents pour le chemin:', currentPath);
    setLoading(true);
    setError(null);
    try {
      const nodes = await getNodes(currentPath);
      console.log('✅ Documents récupérés:', nodes.length, 'nœuds');
      console.log('📂 Premiers nœuds:', nodes.slice(0, 3));
      setDocuments(nodes);
    } catch (err) {
      console.error('❌ Erreur lors du chargement des documents:', err);
      console.error('Détails de l\'erreur:', {
        message: err.message,
        response: err.response,
        status: err.response?.status,
        data: err.response?.data
      });
      setError(`Impossible de charger les documents. ${err.response?.data?.error?.briefSummary || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      loadDocuments();
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const results = await searchNodes(searchTerm);
      setDocuments(results);
    } catch (err) {
      console.error('Erreur lors de la recherche:', err);
      setError('Erreur lors de la recherche. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  const handleNavigate = (nodeId, nodeName) => {
    setCurrentPath(nodeId);
    setBreadcrumbs([...breadcrumbs, { id: nodeId, name: nodeName }]);
  };

  const handleBreadcrumbClick = (index) => {
    const newBreadcrumbs = breadcrumbs.slice(0, index + 1);
    setBreadcrumbs(newBreadcrumbs);
    setCurrentPath(newBreadcrumbs[newBreadcrumbs.length - 1].id);
  };

  const handleViewDetails = (node) => {
    setSelectedNode(node);
    setDetailDialogOpen(true);
  };

  const handleDownload = async (nodeId, name) => {
    try {
      await downloadNode(nodeId, name);
    } catch (err) {
      console.error('Erreur lors du téléchargement:', err);
      setError('Erreur lors du téléchargement du fichier.');
    }
  };

  // Obtenir l'icône selon le type de fichier
  const getFileIcon = (node) => {
    if (node.isFolder) {
      return <Folder sx={{ fontSize: 40, color: '#FFA726' }} />;
    }

    const mimeType = node.content?.mimeType || '';
    const name = node.name.toLowerCase();

    if (mimeType.includes('pdf') || name.endsWith('.pdf')) {
      return <PictureAsPdf sx={{ fontSize: 40, color: '#D32F2F' }} />;
    }
    if (mimeType.includes('image') || /\.(jpg|jpeg|png|gif|bmp|svg)$/.test(name)) {
      return <Image sx={{ fontSize: 40, color: '#7E57C2' }} />;
    }
    if (mimeType.includes('text') || /\.(txt|log)$/.test(name)) {
      return <Description sx={{ fontSize: 40, color: '#42A5F5' }} />;
    }
    if (/\.(zip|rar|7z|tar|gz)$/.test(name)) {
      return <Archive sx={{ fontSize: 40, color: '#8D6E63' }} />;
    }
    if (/\.(js|ts|jsx|tsx|java|py|html|css|json|xml)$/.test(name)) {
      return <Code sx={{ fontSize: 40, color: '#66BB6A' }} />;
    }

    return <InsertDriveFile sx={{ fontSize: 40, color: '#78909C' }} />;
  };

  // Formater la taille du fichier
  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  };

  // Filtrer et trier les documents
  const getFilteredAndSortedDocuments = () => {
    let filtered = [...documents];

    // Filtre par type
    if (filterType !== 'all') {
      filtered = filtered.filter(doc => {
        if (filterType === 'folder') return doc.isFolder;
        if (filterType === 'file') return doc.isFile;
        return true;
      });
    }

    // Tri
    filtered.sort((a, b) => {
      let aValue, bValue;

      switch (sortBy) {
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case 'modifiedAt':
          aValue = new Date(a.modifiedAt);
          bValue = new Date(b.modifiedAt);
          break;
        case 'size':
          aValue = a.content?.sizeInBytes || 0;
          bValue = b.content?.sizeInBytes || 0;
          break;
        case 'createdAt':
          aValue = new Date(a.createdAt);
          bValue = new Date(a.createdAt);
          break;
        default:
          return 0;
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  };

  const filteredDocuments = getFilteredAndSortedDocuments();

  return (
    <Box>
      {/* En-tête */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main', mb: 1 }}>
          Gestion des Documents
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
          Parcourez, recherchez et organisez vos documents Alfresco
        </Typography>
      </Box>

      {/* Fil d'Ariane */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Folder sx={{ color: 'primary.main', mr: 1 }} />
          {breadcrumbs.map((crumb, index) => (
            <React.Fragment key={crumb.id}>
              {index > 0 && <Typography sx={{ mx: 1, color: 'text.secondary' }}>/</Typography>}
              <Chip
                label={crumb.name}
                onClick={() => handleBreadcrumbClick(index)}
                sx={{
                  cursor: index < breadcrumbs.length - 1 ? 'pointer' : 'default',
                  backgroundColor: index === breadcrumbs.length - 1 ? 'primary.main' : 'grey.200',
                  color: index === breadcrumbs.length - 1 ? 'white' : 'text.primary',
                  '&:hover': {
                    backgroundColor: index < breadcrumbs.length - 1 ? 'grey.300' : 'primary.main'
                  }
                }}
              />
            </React.Fragment>
          ))}
        </Box>
      </Paper>

      {/* Barre de recherche et filtres */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Rechercher des documents..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                value={filterType}
                label="Type"
                onChange={(e) => setFilterType(e.target.value)}
                startAdornment={
                  <InputAdornment position="start">
                    <FilterList />
                  </InputAdornment>
                }
              >
                <MenuItem value="all">Tous</MenuItem>
                <MenuItem value="folder">Dossiers</MenuItem>
                <MenuItem value="file">Fichiers</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Trier par</InputLabel>
              <Select
                value={sortBy}
                label="Trier par"
                onChange={(e) => setSortBy(e.target.value)}
                startAdornment={
                  <InputAdornment position="start">
                    <Sort />
                  </InputAdornment>
                }
              >
                <MenuItem value="name">Nom</MenuItem>
                <MenuItem value="modifiedAt">Date de modification</MenuItem>
                <MenuItem value="createdAt">Date de création</MenuItem>
                <MenuItem value="size">Taille</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Ordre</InputLabel>
              <Select
                value={sortOrder}
                label="Ordre"
                onChange={(e) => setSortOrder(e.target.value)}
              >
                <MenuItem value="asc">Croissant</MenuItem>
                <MenuItem value="desc">Décroissant</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Button
              fullWidth
              variant="contained"
              onClick={handleSearch}
              sx={{ height: 56 }}
            >
              Rechercher
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Actions */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {filteredDocuments.length} document(s)
        </Typography>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={loadDocuments}
        >
          Actualiser
        </Button>
      </Box>

      {/* Affichage des erreurs */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Liste des documents */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : filteredDocuments.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 2 }}>
          <Typography variant="h6" color="text.secondary">
            Aucun document trouvé
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {filteredDocuments.map((doc) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={doc.id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.3s ease',
                  cursor: doc.isFolder ? 'pointer' : 'default',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4
                  }
                }}
                onClick={() => doc.isFolder && handleNavigate(doc.id, doc.name)}
              >
                <CardContent sx={{ flexGrow: 1, textAlign: 'center', pt: 3 }}>
                  <Box sx={{ mb: 2 }}>
                    {getFileIcon(doc)}
                  </Box>
                  <Tooltip title={doc.name}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 600,
                        mb: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {doc.name}
                    </Typography>
                  </Tooltip>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'center' }}>
                    {doc.isFile && (
                      <Chip
                        size="small"
                        label={formatFileSize(doc.content?.sizeInBytes)}
                        sx={{ fontSize: '0.75rem' }}
                      />
                    )}
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <CalendarToday sx={{ fontSize: 14 }} />
                      {formatDistanceToNow(new Date(doc.modifiedAt), { addSuffix: true, locale: fr })}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Person sx={{ fontSize: 14 }} />
                      {doc.modifiedByUser?.displayName || 'Inconnu'}
                    </Typography>
                  </Box>
                </CardContent>
                {doc.isFile && (
                  <CardActions sx={{ justifyContent: 'center', pb: 2 }}>
                    <Tooltip title="Voir les détails">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewDetails(doc);
                        }}
                        sx={{ color: 'primary.main' }}
                      >
                        <Visibility />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Télécharger">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownload(doc.id, doc.name);
                        }}
                        sx={{ color: 'success.main' }}
                      >
                        <CloudDownload />
                      </IconButton>
                    </Tooltip>
                  </CardActions>
                )}
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Dialog des détails */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ backgroundColor: 'primary.main', color: 'white', fontWeight: 700 }}>
          Détails du document
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {selectedNode && (
            <List>
              <ListItem>
                <ListItemIcon>{getFileIcon(selectedNode)}</ListItemIcon>
                <ListItemText
                  primary="Nom"
                  secondary={selectedNode.name}
                  primaryTypographyProps={{ fontWeight: 600 }}
                />
              </ListItem>
              <Divider />
              <ListItem>
                <ListItemText
                  primary="Type MIME"
                  secondary={selectedNode.content?.mimeType || 'N/A'}
                  primaryTypographyProps={{ fontWeight: 600 }}
                />
              </ListItem>
              <Divider />
              <ListItem>
                <ListItemText
                  primary="Taille"
                  secondary={formatFileSize(selectedNode.content?.sizeInBytes)}
                  primaryTypographyProps={{ fontWeight: 600 }}
                />
              </ListItem>
              <Divider />
              <ListItem>
                <ListItemText
                  primary="Créé le"
                  secondary={new Date(selectedNode.createdAt).toLocaleString('fr-FR')}
                  primaryTypographyProps={{ fontWeight: 600 }}
                />
              </ListItem>
              <Divider />
              <ListItem>
                <ListItemText
                  primary="Modifié le"
                  secondary={new Date(selectedNode.modifiedAt).toLocaleString('fr-FR')}
                  primaryTypographyProps={{ fontWeight: 600 }}
                />
              </ListItem>
              <Divider />
              <ListItem>
                <ListItemText
                  primary="Créé par"
                  secondary={selectedNode.createdByUser?.displayName || 'Inconnu'}
                  primaryTypographyProps={{ fontWeight: 600 }}
                />
              </ListItem>
              <Divider />
              <ListItem>
                <ListItemText
                  primary="Modifié par"
                  secondary={selectedNode.modifiedByUser?.displayName || 'Inconnu'}
                  primaryTypographyProps={{ fontWeight: 600 }}
                />
              </ListItem>
              <Divider />
              <ListItem>
                <ListItemText
                  primary="ID"
                  secondary={selectedNode.id}
                  primaryTypographyProps={{ fontWeight: 600 }}
                />
              </ListItem>
            </List>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDetailDialogOpen(false)}>Fermer</Button>
          {selectedNode && (
            <Button
              variant="contained"
              startIcon={<CloudDownload />}
              onClick={() => {
                handleDownload(selectedNode.id, selectedNode.name);
                setDetailDialogOpen(false);
              }}
            >
              Télécharger
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DocumentSorting;
